import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Check, AlertTriangle, ChevronDown, Info } from "lucide-react";

const MODEL_NOTE = {
  hourly: "Hourly work — you set the rate and your own minimum booking duration.",
  hourly_or_quote: "Standard jobs are booked at your hourly rate. Heavy or complex jobs come to you as an in-app quote request.",
  instant_or_quote: "Standard work can be instantly bookable. Complex contracts come to you as an in-app quote request.",
  property: "Fixed complete-job pricing by property size — never forced into an hourly rate.",
  turnover: "Fixed per-turnover pricing, with linen and laundry priced separately.",
  options: "Fixed prices per appliance type.",
  per_room: "Priced per room or area, with your own minimum job value.",
  per_property: "Priced by property type — customers never measure individual windows.",
};

const required = (field, cfg) => {
  if (!field.required) return false;
  if (field.requires && !(cfg?.[field.requires] ?? true)) return false;
  return true;
};

export const missingFor = (spec, cfg) =>
  (spec.fields || []).filter((f) => {
    if (!required(f, cfg)) return false;
    if (f.type === "bool") return false;
    if (f.type === "multi") return !(cfg?.[f.key]?.length);
    const v = parseFloat(cfg?.[f.key]);
    return !(v > 0);
  });

/** Spec-driven per-service pricing form. Only the cleaner's selected services are shown. */
export default function ServicePricingEditor({ services = [], pricing, onChange, onStatus,
  showSave = false, testid = "service-pricing" }) {
  const [specs, setSpecs] = useState(null);
  const [minRate, setMinRate] = useState(0);
  const [open, setOpen] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!services.length) { setSpecs([]); return; }
    api.get("/pricing/specs", { params: { services: services.join(",") } }).then((r) => {
      setSpecs(r.data.specs);
      setMinRate(r.data.min_hourly_rate);
      setOpen((o) => o || r.data.specs[0]?.slug);
    });
  }, [services.join(",")]); // eslint-disable-line react-hooks/exhaustive-deps

  // Seed declared defaults once the specs arrive so toggles/durations aren't blank.
  useEffect(() => {
    if (!specs?.length) return;
    const next = { ...(pricing || {}) };
    let changed = false;
    specs.forEach((s) => {
      const cfg = { ...(next[s.slug] || {}) };
      s.fields.forEach((f) => {
        if (f.default !== undefined && cfg[f.key] === undefined) { cfg[f.key] = f.default; changed = true; }
      });
      next[s.slug] = cfg;
    });
    if (changed) onChange?.(next);
  }, [specs]); // eslint-disable-line react-hooks/exhaustive-deps

  const status = useMemo(() => {
    if (!specs) return { complete: false, incomplete: [], byService: {} };
    const byService = {};
    specs.forEach((s) => { byService[s.slug] = missingFor(s, pricing?.[s.slug]); });
    const incomplete = specs.filter((s) => byService[s.slug].length).map((s) => s.slug);
    return { complete: specs.length > 0 && incomplete.length === 0, incomplete, byService };
  }, [specs, pricing]);

  useEffect(() => { onStatus?.(status); }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (slug, key, value) =>
    onChange?.({ ...(pricing || {}), [slug]: { ...(pricing?.[slug] || {}), [key]: value } });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/cleaners/me/pricing", { pricing: pricing || {}, services });
      toast.success("Pricing saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (!specs) return <Loader2 className="h-6 w-6 animate-spin text-accent" />;
  if (!specs.length) return (
    <p className="text-sm text-muted-foreground" data-testid={`${testid}-empty`}>
      Choose your services first — then you'll set a price for each one.
    </p>
  );

  return (
    <div className="space-y-3" data-testid={testid}>
      <div className={`rounded-2xl border p-4 text-sm ${status.complete ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"}`} data-testid={`${testid}-status`}>
        <p className="font-semibold inline-flex items-center gap-2">
          {status.complete ? <Check className="h-4 w-4 text-emerald-600" /> : <AlertTriangle className="h-4 w-4 text-amber-600" />}
          {status.complete ? "All good ✓ — every selected service is priced"
            : `Pricing still needed for ${status.incomplete.length} of your ${specs.length} service(s)`}
        </p>
        <p className="text-muted-foreground mt-1">
          Every service has its own pricing — an hourly rate never applies to a fixed-price service.
          You can change any of this later from your dashboard.
        </p>
      </div>

      {specs.map((s) => {
        const cfg = pricing?.[s.slug] || {};
        const missing = status.byService[s.slug] || [];
        const isOpen = open === s.slug;
        return (
          <div key={s.slug} className="bg-card rounded-2xl border border-primary/5 overflow-hidden">
            <button type="button" onClick={() => setOpen(isOpen ? null : s.slug)}
              className="w-full flex items-center gap-3 p-4 text-left hover:bg-secondary/40 transition-colors"
              data-testid={`pricing-toggle-${s.slug}`}>
              <span className={`h-6 w-6 rounded-full flex items-center justify-center shrink-0 ${missing.length ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>
                {missing.length ? <AlertTriangle className="h-3.5 w-3.5" /> : <Check className="h-3.5 w-3.5" />}
              </span>
              <span className="min-w-0">
                <span className="font-medium block">{s.name}</span>
                <span className="text-xs text-muted-foreground block truncate">
                  {missing.length ? `Needs: ${missing.map((m) => m.label).join(", ")}` : "Priced"}
                </span>
              </span>
              <ChevronDown className={`h-4 w-4 ml-auto shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`} />
            </button>
            {isOpen && (
              <div className="px-4 pb-5 space-y-4" data-testid={`pricing-fields-${s.slug}`}>
                <p className="text-xs text-muted-foreground flex gap-1.5">
                  <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" /> {MODEL_NOTE[s.model]}
                </p>
                <div className="grid sm:grid-cols-2 gap-4">
                  {s.fields.map((f) => {
                    if (f.requires && !(cfg[f.requires] ?? true)) return null;
                    if (f.requires_capability && !(cfg.capabilities || []).includes(f.requires_capability)) return null;
                    const label = `${f.label}${required(f, cfg) ? " *" : ""}`;
                    if (f.type === "bool") {
                      const on = cfg[f.key] ?? f.default ?? false;
                      return (
                        <label key={f.key} className="sm:col-span-2 flex items-start gap-2.5 text-sm p-3 rounded-xl border border-border cursor-pointer">
                          <input type="checkbox" checked={!!on} className="mt-0.5"
                            onChange={(e) => set(s.slug, f.key, e.target.checked)}
                            data-testid={`price-${s.slug}-${f.key}`} />
                          <span>{f.label}{f.help && <span className="block text-xs text-muted-foreground">{f.help}</span>}</span>
                        </label>
                      );
                    }
                    if (f.type === "multi") {
                      const selected = cfg[f.key] || f.default || [];
                      return (
                        <div key={f.key} className="sm:col-span-2">
                          <p className="text-sm font-medium">{label}</p>
                          <div className="flex flex-wrap gap-2 mt-2">
                            {f.choices.map((c) => {
                              const on = selected.includes(c.key);
                              return (
                                <button key={c.key} type="button"
                                  onClick={() => set(s.slug, f.key, on ? selected.filter((x) => x !== c.key) : [...selected, c.key])}
                                  className={`px-3.5 py-2 rounded-full border-2 text-sm transition-colors ${on ? "border-accent bg-accent/10 text-accent font-medium" : "border-border hover:border-accent/40"}`}
                                  data-testid={`price-${s.slug}-${f.key}-${c.key}`}>
                                  {on && <Check className="h-3.5 w-3.5 inline mr-1" />}{c.label}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    }
                    if (f.type === "hours") {
                      return (
                        <label key={f.key} className="text-sm">{label}
                          <select value={cfg[f.key] ?? f.default ?? 2}
                            onChange={(e) => set(s.slug, f.key, parseFloat(e.target.value))}
                            className="sp-input" data-testid={`price-${s.slug}-${f.key}`}>
                            {(f.options || []).map((h) => (
                              <option key={h} value={h}>{h === 1 ? "1 hour" : `${h} hours`}{h === 2 ? " (recommended)" : ""}</option>
                            ))}
                          </select>
                          {f.help && <span className="block text-xs text-muted-foreground mt-1">{f.help}</span>}
                        </label>
                      );
                    }
                    return (
                      <label key={f.key} className="text-sm">{label}
                        <input type="number" min="0" step="0.5" inputMode="decimal"
                          value={cfg[f.key] ?? ""} placeholder={f.rate ? `min £${minRate}` : "£"}
                          onChange={(e) => set(s.slug, f.key, e.target.value === "" ? "" : parseFloat(e.target.value))}
                          className="sp-input" data-testid={`price-${s.slug}-${f.key}`} />
                        {f.help && <span className="block text-xs text-muted-foreground mt-1">{f.help}</span>}
                        {f.rate && cfg[f.key] > 0 && cfg[f.key] < minRate && (
                          <span className="block text-xs text-destructive mt-1">
                            Below the £{minRate}/hour platform minimum.
                          </span>
                        )}
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      })}

      {showSave && (
        <button onClick={save} disabled={saving}
          className="px-6 py-3 rounded-full bg-accent text-white font-medium inline-flex items-center gap-2 disabled:opacity-50 hover:bg-accent/90 transition-colors"
          data-testid={`${testid}-save`}>
          {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save pricing
        </button>
      )}
      <style>{`.sp-input{width:100%;margin-top:0.25rem;padding:0.625rem 0.75rem;border-radius:0.75rem;border:1px solid hsl(var(--border));background:hsl(var(--background));outline:none}.sp-input:focus{border-color:hsl(var(--accent))}`}</style>
    </div>
  );
}
